import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RechnungDropzone } from '../components/kanzlei/RechnungDropzone';
import { RechnungPdfVorschau } from '../components/kanzlei/RechnungPdfVorschau';
import { RechnungStapel, bereit } from '../components/kanzlei/RechnungStapel';
import { JahreszusatzUmstellen } from '../components/kanzlei/JahreszusatzUmstellen';
import Header from '../components/Header';
import { api } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { DatumFeld } from '../components/DatumFeld';
import { useMandantNachNummer, MandantTreffer } from '../components/kanzlei/MandantNachNummer';
import { RechnungenVorlage } from '../components/kanzlei/ZahlungenVorlage';

const eur = (v) => Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const inp = 'h-10 px-3 border border-gray-300 rounded-[10px] text-sm bg-white';
const LEER = { mandanten_nr: '', rechnungsnummer: '', betrag: '', faellig_am: '', case_id: '', lastschrift: false };
let zaehler = 0;

const mitJahr = (nummer, rechnungsdatum) => {
  const n = String(nummer || '').trim();
  const jj = String((rechnungsdatum ? Number(rechnungsdatum.slice(0, 4)) : new Date().getFullYear()) % 100).padStart(2, '0');
  const m = n.match(/^(\d{1,6})\s*[/_-]\s*(\d{4}|\d{2})$/);
  if (m) return `${Number(m[1])}/${m[2].slice(-2)}`;
  return /^\d{1,6}$/.test(n) ? `${Number(n)}/${jj}` : n;
};

const ausErkennung = (data, basis) => {
  const neu = {};
  if (data.mandanten_nr) neu.mandanten_nr = data.mandanten_nr;
  if (data.case_id) neu.case_id = data.case_id;
  if (data.rechnungsnummer) neu.rechnungsnummer = data.rechnungsnummer;
  if (data.betrag) neu.betrag = Number(data.betrag).toFixed(2).replace('.', ',');
  if (data.faellig_am) neu.faellig_am = data.faellig_am;
  if (data.lastschrift) neu.lastschrift = true;
  return { f: { ...LEER, mandanten_nr: basis.mandanten_nr, ...neu }, erkannt: Object.keys(neu) };
};

export default function KanzleiRechnungen() {
  const { toast } = useToast();
  const [params] = useSearchParams();
  const [basis, setBasis] = useState(() => ({ ...LEER, mandanten_nr: (params.get('mnr') || '').replace(/\D/g, '').slice(0, 5) }));
  const [stapel, setStapel] = useState([]);
  const [aktivId, setAktivId] = useState(null);
  const [akten, setAkten] = useState([]);
  const [stand, setStand] = useState(0);
  const [busy, setBusy] = useState(false);
  const aktiv = stapel.find((s) => s.id === aktivId);
  const f = aktiv ? aktiv.f : basis;
  const setF = (neu) => (aktiv ? setStapel((xs) => xs.map((s) => (s.id === aktiv.id ? { ...s, f: neu, ...(s.status === 'fehler' ? { status: 'bereit', fehler: '' } : {}) } : s))) : setBasis(neu));
  const file = aktiv?.file || null;
  const erkannt = aktiv?.erkannt || [];
  const info = aktiv?.info || null;
  const lesen = aktiv?.status === 'lesen';
  const treffer = useMandantNachNummer(f.mandanten_nr);
  const load = useCallback(() => setStand((x) => x + 1), []);
  useEffect(() => {
    if (!treffer.m) { setAkten([]); return; }
    api.get('/cases').then((r) => setAkten((r.data.items || []).filter((c) => c.mandanten_nr === treffer.m.mandanten_nr && !String(c.id).startsWith('general_')).map((c) => ({ case_id: c.id, aktenzeichen: c.aktenzeichen, rechtsgebiet: c.rechtsgebiet })))).catch(() => setAkten([]));
  }, [treffer.m]);
  const ring = (k) => (erkannt.includes(k) ? ' ring-2 ring-[#e0b56a] border-[#b8893d]' : '');
  const aendern = (id, upd) => setStapel((xs) => xs.map((s) => (s.id === id ? { ...s, ...upd } : s)));
  const dateien = async (liste) => {
    const pdfs = liste.filter((d) => d.type === 'application/pdf' || d.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length < liste.length) toast({ title: `${liste.length - pdfs.length} Datei(en) übersprungen – nur PDF möglich`, variant: 'destructive' });
    if (!pdfs.length) return;
    const neu = pdfs.map((d) => ({ id: `s${(zaehler += 1)}`, file: d, f: { ...LEER, mandanten_nr: basis.mandanten_nr }, erkannt: [], info: null, status: 'lesen' }));
    setStapel((xs) => [...xs.filter((s) => s.status !== 'fertig'), ...neu]);
    if (!aktiv || aktiv.status === 'fertig') setAktivId(neu[0].id);
    for (const s of neu) {
      const fd = new FormData(); fd.append('file', s.file);
      try {
        const { data } = await api.post('/kanzlei/rechnungen/erkennen', fd);
        aendern(s.id, { info: data, ...ausErkennung(data, basis), status: 'bereit' });
      } catch (err) { aendern(s.id, { status: 'bereit', fehler: '' }); }
    }
    if (neu.length === 1) toast({ title: 'Rechnung gelesen – bitte Angaben mit der Vorschau prüfen' });
    else toast({ title: `${neu.length} Rechnungen gelesen – bitte nacheinander prüfen oder alle vollständigen bereitstellen` });
  };
  const hochladen = async (s) => {
    const fd = new FormData();
    Object.entries(s.f).forEach(([k, v]) => fd.append(k, String(v)));
    if (s.info?.rechnungsdatum) fd.append('rechnungsdatum', s.info.rechnungsdatum);
    fd.set('betrag', String(s.f.betrag).replace(',', '.'));
    fd.append('file', s.file);
    const r = await api.post('/kanzlei/rechnungen', fd);
    aendern(s.id, { status: 'fertig' });
    return r.data;
  };
  const naechste = (ohne) => stapel.find((s) => !ohne.includes(s.id) && s.status === 'bereit');
  const senden = async (e) => {
    e.preventDefault();
    if (!aktiv) { toast({ title: 'Bitte die Rechnung als PDF ablegen', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const r = await hochladen(aktiv);
      toast({ title: `Rechnung bereitgestellt · offen ${eur(r.offen)}` });
      setAktivId(naechste([aktiv.id])?.id || null); load();
    } catch (err) { toast({ title: err?.response?.data?.detail || 'Hochladen fehlgeschlagen', variant: 'destructive' }); } finally { setBusy(false); }
  };
  const alleSenden = async () => {
    const liste = stapel.filter(bereit);
    if (!window.confirm(`${liste.length} Rechnungen jetzt bei den Mandanten bereitstellen?\nJeder Mandant erhält eine E-Mail.`)) return;
    setBusy(true);
    let ok = 0;
    for (const s of liste) {
      try { await hochladen(s); ok += 1; } catch (err) { aendern(s.id, { status: 'fehler', fehler: err?.response?.data?.detail || 'Fehler' }); }
    }
    setBusy(false); load();
    setAktivId(null);
    toast({ title: `${ok} von ${liste.length} Rechnungen bereitgestellt`, variant: ok < liste.length ? 'destructive' : undefined });
  };
  return (
    <div className="min-h-screen bg-[#f5f8fa]" data-testid="kanzlei-rechnungen">
      <Header />
      <main className={`pt-[104px] lg:pt-[152px] ${file ? 'max-w-[1500px]' : 'max-w-[1200px]'} mx-auto px-4 lg:px-8 pb-16`}>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1B3A5C] pt-10" data-testid="rechnungen-titel">Rechnungen</h1>
        <h2 className="font-serif text-2xl text-[#1B3A5C] mt-6" data-testid="rechnung-bereitstellen-titel">Rechnung bereitstellen</h2>
        <p className="text-sm text-[#34536b] mt-2 mb-4">Rechnung aus DATEV als PDF beim Mandanten bereitstellen. Der Mandant bezahlt im Portal. Akontozahlungen und Überweisungen ordnen Sie danach an der Rechnung zu – der offene Betrag ermäßigt sich entsprechend.</p>
        <form onSubmit={senden} className={`bg-white border border-gray-200 rounded-2xl p-6 grid gap-5 mb-8 ${file ? 'lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]' : ''}`} data-testid="rechnung-form">
          <div className={file ? 'lg:col-span-2' : ''}><RechnungDropzone datei={file} busy={lesen} onDateien={dateien} erkannt={erkannt} /></div>
          <RechnungStapel stapel={stapel} aktivId={aktivId} onWaehlen={setAktivId} busy={busy} onAlle={alleSenden}
            onEntfernen={(id) => { setStapel((xs) => xs.filter((s) => s.id !== id)); if (id === aktivId) setAktivId(naechste([id])?.id || null); }} />
          {file && <RechnungPdfVorschau datei={file} info={info} />}
          <div className={`grid gap-3 content-start ${file ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
          {file && <p className="sm:col-span-2 text-xs text-[#34536b]">Bitte die Angaben mit der Vorschau abgleichen. Gold umrandete Felder wurden automatisch erkannt.</p>}
          <div><input className={`${inp} w-full font-mono${ring('mandanten_nr')}`} inputMode="numeric" placeholder="Mandantennummer *" value={f.mandanten_nr} onChange={(e) => setF({ ...f, mandanten_nr: e.target.value.replace(/\D/g, '').slice(0, 5), case_id: '' })} data-testid="rechnung-mnr" />
            <div className="mt-1"><MandantTreffer st={treffer} testId="rechnung-mnr-treffer" /></div></div>
          <select className={`${inp}${ring('case_id')}`} value={f.case_id} onChange={(e) => setF({ ...f, case_id: e.target.value })} data-testid="rechnung-akte">
            <option value="">Akte (optional)</option>
            {akten.map((a) => <option key={a.case_id} value={a.case_id}>{a.aktenzeichen || a.case_id} {a.rechtsgebiet ? `· ${a.rechtsgebiet}` : ''}</option>)}
          </select>
          <div><input className={`${inp} w-full${ring('rechnungsnummer')}`} placeholder="Rechnungsnummer *" value={f.rechnungsnummer} onChange={(e) => setF({ ...f, rechnungsnummer: e.target.value })} onBlur={() => f.rechnungsnummer && info?.rechnungsdatum && setF({ ...f, rechnungsnummer: mitJahr(f.rechnungsnummer, info.rechnungsdatum) })} required data-testid="rechnung-nummer" />
            <p className="mt-1 text-[10px] text-gray-500">Das Jahr wird automatisch aus dem Rechnungsdatum ergänzt{info?.rechnungsdatum ? `, z. B. 1234 → 1234/${info.rechnungsdatum.slice(2, 4)}` : ' (z. B. 1234 → 1234/26)'}</p></div>
          <input className={`${inp}${ring('betrag')}`} placeholder="Betrag brutto (€) *" inputMode="decimal" value={f.betrag} onChange={(e) => setF({ ...f, betrag: e.target.value })} required data-testid="rechnung-betrag" />
          <DatumFeld className={`${inp}${ring('faellig_am')}`} value={f.faellig_am} onChange={(v) => setF({ ...f, faellig_am: v })} testId="rechnung-faellig" placeholder="Fällig am (TT.MM.JJJJ) *" />
          <label className={`flex items-center gap-2 text-sm text-[#1B3A5C] sm:col-span-2 rounded-[10px] ${ring('lastschrift')}`}><input type="checkbox" checked={!!f.lastschrift} onChange={(e) => setF({ ...f, lastschrift: e.target.checked })} className="accent-[#b8893d]" data-testid="rechnung-lastschrift-check" />Wird per SEPA-Lastschrift eingezogen (Mandant sieht „wird eingezogen“ statt Zahlknopf, keine Mahnungen)</label>
          <button type="submit" disabled={busy || !treffer.m || !file || lesen} className="h-10 px-5 rounded-[10px] bg-[#1B3A5C] text-white text-sm disabled:opacity-50" data-testid="rechnung-senden">{busy ? 'Wird hochgeladen …' : 'Beim Mandanten bereitstellen'}</button>
          {file && <button type="button" onClick={() => { setStapel((xs) => xs.filter((s) => s.id !== aktivId)); setAktivId(naechste([aktivId])?.id || null); }} className="h-10 px-4 rounded-[10px] border border-gray-200 text-sm text-[#7a8a99] hover:text-[#1B3A5C]" data-testid="rechnung-verwerfen">Verwerfen</button>}
          </div>
        </form>
        <div className="flex justify-end mb-3"><JahreszusatzUmstellen toast={toast} onDone={load} /></div>
        <RechnungenVorlage key={stand} />
      </main>
      <Toaster />
    </div>
  );
}
